import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { LogOut, User, ShoppingCart, Upload, Headphones, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PublisherDashboard = ({ publisher, onLogout }) => {
  const handleLinkClick = (url, title) => {
    if (!url || url.trim() === "") {
      toast.error(`${title} link not configured. Contact your administrator.`);
      return;
    }
    
    // Handle mailto links differently for iOS compatibility
    if (url.startsWith('mailto:')) {
      try {
        // Use window.location.href for better iOS compatibility
        window.location.href = url;
      } catch (error) {
        // Fallback: copy email to clipboard and show toast
        const email = url.replace('mailto:', '');
        if (navigator.clipboard) {
          navigator.clipboard.writeText(email).then(() => {
            toast.success(`Email kopierad till clipboard: ${email}`);
          }).catch(() => {
            toast.error(`Kunde inte öppna email. Maila: ${email}`);
          });
        } else {
          toast.error(`Kunde inte öppna email. Maila: ${email}`);
        }
      }
    } else {
      // For regular URLs, use window.open
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const options = [
    {
      title: "Lägg beställning",
      description: "Skapa en ny beställning för ljudbok",
      icon: ShoppingCart,
      url: publisher?.order_link,
      color: "from-blue-500 to-indigo-600",
      testId: "order-link"
    },
    {
      title: "Ladda upp manus",
      description: "Ladda upp ditt manuskript för ljudbok",
      icon: Upload,
      url: publisher?.upload_manuscript_link,
      color: "from-green-500 to-teal-600",
      testId: "upload-link"
    },
    {
      title: "Se beställda ljudböcker",
      description: "Visa status för dina beställningar",
      icon: Headphones,
      url: publisher?.view_audiobooks_link,
      color: "from-purple-500 to-pink-600",
      testId: "audiobooks-link"
    },
    {
      title: "Kontakta produktions-avdelningen",
      description: "",
      icon: Mail,
      url: publisher?.email_production_link || "mailto:produktioner@visueltljud.se",
      color: "from-orange-500 to-red-600",
      testId: "email-production-link"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="publisher-name">
                  {publisher.name}
                </h1>
                <p className="text-sm text-gray-600">Välj vad du vill göra</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={onLogout}
              className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span>Logga ut</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Välkommen tillbaka!</h2>
          <p className="text-xl text-gray-600">Vad vill du göra idag?</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {options.map((option, index) => {
            const IconComponent = option.icon;
            return (
              <Card 
                key={index}
                className="project-card cursor-pointer group hover:scale-105 transform transition-all duration-200"
                onClick={() => handleLinkClick(option.url, option.title)}
                data-testid={option.testId}
              >
                <CardHeader className="text-center pb-4">
                  <div className={`mx-auto w-16 h-16 bg-gradient-to-br ${option.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                    {option.title === "Kontakta produktions-avdelningen" ? (
                      <>
                        Kontakta<br />
                        produktions-<br />
                        avdelningen
                      </>
                    ) : (
                      option.title
                    )}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {option.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="flex items-center justify-center text-sm text-gray-500 group-hover:text-indigo-500 transition-colors">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    <span>Klicka för att öppna</span>
                  </div>
                  {(!option.url || option.url.trim() === "") && (
                    <div className="mt-2 text-xs text-red-500">
                      Länk ej konfigurerad
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Information */}
        <div className="mt-16 text-center">
          <Card className="glass-card max-w-2xl mx-auto">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Behöver du hjälp?</h3>
              <p className="text-gray-600 mb-4">
                Om någon av länkarna inte fungerar, kontakta din administratör för att få dem konfigurerade.
              </p>
              <p className="text-gray-900 font-medium">
                Ring: 08-644 08 50
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PublisherDashboard;
